import { Skeleton, Typography } from '@/components/atoms';
import { useTranslation, useUIColors } from '@/core/hooks';
import {
  type AppointmentSlotCta,
  type ReservationSlotCta,
  RESERVATION_CTA_I18N_KEY,
} from '@/core/reservation';
import React from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface PropertyDetailsAdhaStickyFooterProps {
  reservationCta: ReservationSlotCta;
  appointmentCta: AppointmentSlotCta;
  hideReserveCta: boolean;
  hideBookCta: boolean;
  isAppointmentPrimary: boolean;
  banner?: React.ReactNode;
  onReservePress?: () => void;
  onBookPress?: () => void;
  disabled?: boolean;
  /** Live CTA data still loading — show button shimmers instead of guessed buttons. */
  loading?: boolean;
}

/**
 * Footer bar with two button-sized shimmers, same size/position as the real
 * Reserve / Book buttons, so they fade in instead of popping in.
 */
export function PropertyDetailsAdhaStickyFooterSkeleton({
  banner,
}: Readonly<{ banner?: React.ReactNode }>) {
  const colors = useUIColors();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={[
        styles.stickyFooterWrap,
        {
          backgroundColor:
            Platform.OS === 'ios' ? 'transparent' : colors.backgroundLevel1,
        },
      ]}
    >
      {banner}
      <View
        style={[
          styles.bottomBar,
          {
            backgroundColor: colors.backgroundLevel1,
            paddingBottom: 20 + insets.bottom,
          },
        ]}
      >
        <View style={styles.skeletonSlot}>
          <Skeleton height={48} borderRadius={4} />
        </View>
        <View style={styles.skeletonSlot}>
          <Skeleton height={48} borderRadius={4} />
        </View>
      </View>
    </View>
  );
}

export function PropertyDetailsAdhaStickyFooter({
  reservationCta,
  appointmentCta,
  hideReserveCta,
  hideBookCta,
  isAppointmentPrimary,
  banner,
  onReservePress,
  onBookPress,
  disabled = false,
  loading = false,
}: Readonly<PropertyDetailsAdhaStickyFooterProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  if (loading) {
    return <PropertyDetailsAdhaStickyFooterSkeleton banner={banner} />;
  }
  const showBottomBar = !hideReserveCta || !hideBookCta;
  if (!banner && !showBottomBar) {
    return null;
  }

  return (
    <View
      style={[
        styles.stickyFooterWrap,
        {
          backgroundColor:
            Platform.OS === 'ios' ? 'transparent' : colors.backgroundLevel1,
        },
      ]}
    >
      {banner}
      {showBottomBar ? (
        <View
          style={[
            styles.bottomBar,
            {
              backgroundColor: colors.backgroundLevel1,
              paddingBottom: 20 + insets.bottom,
            },
          ]}
        >
          {!hideReserveCta && (
            <Pressable
              disabled={disabled}
              style={[
                styles.bottomActionButton,
                { borderColor: colors.fontPrimary },
                disabled && { opacity: 0.5 },
              ]}
              onPress={onReservePress}
            >
              <Typography
                variant="headline"
                color="primary"
                numberOfLines={1}
                allowFontScaling={false}
                style={styles.bottomActionButtonText}
              >
                {t(RESERVATION_CTA_I18N_KEY[reservationCta])}
              </Typography>
            </Pressable>
          )}
          {!hideBookCta && (
            <Pressable
              disabled={disabled}
              style={[
                styles.bottomActionButton,
                isAppointmentPrimary
                  ? {
                      backgroundColor: colors.buttonDefault,
                      borderColor: colors.buttonDefault,
                    }
                  : { borderColor: colors.fontPrimary },
                disabled && { opacity: 0.5 },
              ]}
              onPress={onBookPress}
            >
              <Typography
                variant="headline"
                color={isAppointmentPrimary ? 'white' : 'primary'}
                numberOfLines={1}
                allowFontScaling={false}
                style={styles.bottomActionButtonText}
              >
                {t(RESERVATION_CTA_I18N_KEY[appointmentCta])}
              </Typography>
            </Pressable>
          )}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  stickyFooterWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  bottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: '#EDEDED',
  },
  bottomActionButton: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    height: 48,
    borderRadius: 4,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    overflow: 'hidden',
  },
  skeletonSlot: {
    flex: 1,
  },
  bottomActionButtonText: {
    width: '100%',
    fontWeight: '700',
    fontSize: 13,
    lineHeight: 16,
    textAlign: 'center',
  },
});

export default PropertyDetailsAdhaStickyFooter;
